import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './WordSearch.css';

const WordSearch = () => {
  const [grid, setGrid] = useState([]);
  const [words, setWords] = useState([]);
  const [foundWords, setFoundWords] = useState(new Set());
  const [selectedCells, setSelectedCells] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [highlightedCells, setHighlightedCells] = useState(new Set());
  const [isComplete, setIsComplete] = useState(false);

  const gridSize = 12;
  
  const [currentTheme, setCurrentTheme] = useState('animals');

  const wordLists = useMemo(() => ({
    animals: ['CAT', 'DOG', 'BIRD', 'FISH', 'LION', 'BEAR', 'WOLF', 'DEER'],
    colors: ['RED', 'BLUE', 'GREEN', 'YELLOW', 'PURPLE', 'ORANGE', 'PINK', 'BLACK'],
    fruits: ['APPLE', 'BANANA', 'ORANGE', 'GRAPE', 'LEMON', 'CHERRY', 'PEACH', 'BERRY'],
    sports: ['SOCCER', 'TENNIS', 'GOLF', 'SWIM', 'RUN', 'JUMP', 'BIKE', 'SURF']
  }), []);

  const generateGrid = useCallback(() => {
    // Initialize empty grid
    const newGrid = Array(gridSize).fill().map(() => Array(gridSize).fill(''));
    const wordsToPlace = wordLists[currentTheme].slice(0, 6); // Use 6 words
    const placedWords = [];

    // Directions: horizontal, vertical, diagonal
    const directions = [
      [0, 1],   // horizontal right
      [1, 0],   // vertical down
      [1, 1],   // diagonal down-right
      [1, -1],  // diagonal down-left
      [0, -1],  // horizontal left
      [-1, 0],  // vertical up
      [-1, -1], // diagonal up-left
      [-1, 1]   // diagonal up-right
    ];

    // Place each word
    wordsToPlace.forEach(word => {
      let placed = false;
      let attempts = 0;
      
      while (!placed && attempts < 100) {
        const direction = directions[Math.floor(Math.random() * directions.length)];
        const [dRow, dCol] = direction;
        
        // Calculate starting position
        const maxStartRow = dRow >= 0 ? gridSize - word.length * Math.abs(dRow) : word.length * Math.abs(dRow) - 1;
        const maxStartCol = dCol >= 0 ? gridSize - word.length * Math.abs(dCol) : word.length * Math.abs(dCol) - 1;
        const minStartRow = dRow < 0 ? word.length * Math.abs(dRow) - 1 : 0;
        const minStartCol = dCol < 0 ? word.length * Math.abs(dCol) - 1 : 0;
        
        if (maxStartRow >= minStartRow && maxStartCol >= minStartCol) {
          const startRow = Math.floor(Math.random() * (maxStartRow - minStartRow + 1)) + minStartRow;
          const startCol = Math.floor(Math.random() * (maxStartCol - minStartCol + 1)) + minStartCol;
          
          // Check if word can be placed
          let canPlace = true;
          const positions = [];
          
          for (let i = 0; i < word.length; i++) {
            const row = startRow + i * dRow;
            const col = startCol + i * dCol;
            
            if (row < 0 || row >= gridSize || col < 0 || col >= gridSize) {
              canPlace = false;
              break;
            }
            
            if (newGrid[row][col] !== '' && newGrid[row][col] !== word[i]) {
              canPlace = false;
              break;
            }
            
            positions.push([row, col]);
          }
          
          if (canPlace) {
            // Place the word
            positions.forEach(([row, col], i) => {
              newGrid[row][col] = word[i];
            });
            
            placedWords.push({
              word,
              positions,
              direction
            });
            placed = true;
          }
        }
        
        attempts++;
      }
    });

    // Fill empty cells with random letters
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        if (newGrid[row][col] === '') {
          newGrid[row][col] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
        }
      }
    }

    setGrid(newGrid);
    setWords(placedWords);
    setFoundWords(new Set());
    setHighlightedCells(new Set());
    setIsComplete(false);
  }, [currentTheme, wordLists]);

  const getCellKey = (row, col) => `${row}-${col}`;

  const handleMouseDown = (row, col) => {
    setIsSelecting(true);
    setSelectedCells([[row, col]]);
  };

  const handleMouseEnter = (row, col) => {
    if (isSelecting && selectedCells.length > 0) {
      const [startRow, startCol] = selectedCells[0];
      const newSelection = getLineCells(startRow, startCol, row, col);
      setSelectedCells(newSelection);
    }
  };

  const handleMouseUp = () => {
    if (selectedCells.length > 1) {
      checkForWord();
    }
    setIsSelecting(false);
    setSelectedCells([]);
  };

  const getLineCells = (startRow, startCol, endRow, endCol) => {
    const cells = [];
    const dRow = endRow - startRow;
    const dCol = endCol - startCol;
    
    // Check if it's a valid line (horizontal, vertical, or diagonal)
    if (dRow === 0 || dCol === 0 || Math.abs(dRow) === Math.abs(dCol)) {
      const steps = Math.max(Math.abs(dRow), Math.abs(dCol));
      const stepRow = steps === 0 ? 0 : dRow / steps;
      const stepCol = steps === 0 ? 0 : dCol / steps;
      
      for (let i = 0; i <= steps; i++) {
        const row = startRow + Math.round(i * stepRow);
        const col = startCol + Math.round(i * stepCol);
        if (row >= 0 && row < gridSize && col >= 0 && col < gridSize) {
          cells.push([row, col]);
        }
      }
    }
    
    return cells;
  };

  const checkForWord = () => {
    const selectedWord = selectedCells.map(([row, col]) => grid[row][col]).join('');
    const reversedWord = selectedWord.split('').reverse().join('');
    
    words.forEach(wordObj => {
      if ((wordObj.word === selectedWord || wordObj.word === reversedWord) && !foundWords.has(wordObj.word)) {
        setFoundWords(prev => new Set([...prev, wordObj.word]));
        
        // Add to highlighted cells
        const newHighlighted = new Set(highlightedCells);
        selectedCells.forEach(([row, col]) => {
          newHighlighted.add(getCellKey(row, col));
        });
        setHighlightedCells(newHighlighted);
        
        // Check if all words are found
        if (foundWords.size + 1 === words.length) {
          setIsComplete(true);
        }
      }
    });
  };

  const newGame = (theme = currentTheme) => {
    setCurrentTheme(theme);
  };

  useEffect(() => {
    generateGrid();
  }, [generateGrid]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsSelecting(false);
      setSelectedCells([]);
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const getCellClass = (row, col) => {
    const key = getCellKey(row, col);
    let className = 'word-search-cell';
    
    if (highlightedCells.has(key)) {
      className += ' found';
    }
    
    if (selectedCells.some(([r, c]) => r === row && c === col)) {
      className += ' selected';
    }
    
    return className;
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🔍 Word Search</h1>
        <div className="game-instructions">
          Find all the hidden words in the grid. Click and drag to select words horizontally, vertically, or diagonally.
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          {isComplete ? '🎉 All Words Found!' : `Found: ${foundWords.size} / ${words.length} words`}
        </div>

        <div className="theme-selector">
          <label>Theme:</label>
          {Object.keys(wordLists).map(theme => (
            <button
              key={theme}
              onClick={() => newGame(theme)}
              className={`btn ${currentTheme === theme ? 'btn-primary' : 'btn-secondary'}`}
            >
              {theme.charAt(0).toUpperCase() + theme.slice(1)}
            </button>
          ))}
        </div>

        <div className="game-play-area">
          <div className="game-content">
            <div className="word-search-grid">
              {grid.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                  <div
                    key={getCellKey(rowIndex, colIndex)}
                    className={getCellClass(rowIndex, colIndex)}
                    onMouseDown={() => handleMouseDown(rowIndex, colIndex)}
                    onMouseEnter={() => handleMouseEnter(rowIndex, colIndex)}
                    onMouseUp={handleMouseUp}
                  >
                    {cell}
                  </div>
                ))
              )}
            </div>

            <div className="word-list">
              <h3>Find these words:</h3>
              <div className="words">
                {words.map(wordObj => (
                  <div
                    key={wordObj.word}
                    className={`word-item ${foundWords.has(wordObj.word) ? 'found' : ''}`}
                  >
                    {wordObj.word}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={() => generateGrid()}>
          Reset Game
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default WordSearch;